import { useContext, useRef, useState } from 'react'
import { Pencil, Check, X } from 'lucide-react'
import {
  GlobalDataContext,
  TemplateContext,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import { searchUsers } from '../../../utils/decoupled'

/**
 * Builder primitive — inline "click-the-pencil to edit a cell" for a listing
 * row, persisted through decoupled_toolbox's generic write API.
 *
 * Two modes :
 *   - `text` (default) : a plain text input. Persists `field` (default `title`).
 *   - `user`           : a username autocomplete backed by la collection decoupled des comptes
 *                        (`[{uid, name}]`). Persists `uidField` (default `uid`)
 *                        with the picked user's id ; the displayed value stays
 *                        the username.
 *
 * It keeps the edit state in local React state (draft value) so Cancel simply
 * discards it — no store gymnastics, no clone, no list refetch. On Save it POSTs
 *
 *   POST /decoupled-api/save?display=decoupled&entity_type=node
 *        &bundle=<row.type>&entity_id=<row.id>
 *   body: { data: { <field>: <value> } }
 *
 * then commits the new display value into the row for instant feedback. CSRF +
 * credentials are injected by `fetchPatch.js` for `/decoupled-api/*`.
 *
 * Server-side prerequisite : the persisted field must be exposed (writable) in
 * the entity's `decoupled` view display.
 *
 *   - type: InlineEditCell
 *     valueLocation: ~.title       # the displayed value (also the text draft)
 *     field: title                 # Drupal field to persist (text mode)
 *     linkLocation: ~.public_path  # optional — render the value as a link in view mode
 *     idLocation: ~.id             # optional, default ~.id
 *     bundleLocation: ~.type       # optional, default ~.type
 *
 *   - type: InlineEditCell
 *     mode: user
 *     valueLocation: ~.author_name
 *     uidField: uid
 */
export const InlineEditCell = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const resolve = (dataLocation, defaultValue = null) =>
    propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation,
      defaultValue,
      globalDataContext,
      templateContext,
    })

  const { formData: displayValue, formDataPath: displayPath } = resolve(props.valueLocation, '')
  // Only resolve a link when explicitly configured (string path), otherwise a
  // null dataLocation could resolve to the whole row and be treated as an href.
  const linkRaw = props.linkLocation ? resolve(props.linkLocation, null).formData : null
  const link = typeof linkRaw === 'string' && linkRaw ? linkRaw : null

  const mode = props.mode ?? 'text'
  const field = props.field ?? 'title'
  const uidField = props.uidField ?? 'uid'
  const entityType = props.entityType ?? 'node'
  const display = props.display ?? 'decoupled'
  const saveUrl = props.saveUrl ?? '/decoupled-api/save'
  const empty = props.empty ?? '—'

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [draftUid, setDraftUid] = useState(null)
  const [busy, setBusy] = useState(false)
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const timer = useRef(null)

  const startEdit = () => {
    setDraft(displayValue ?? '')
    setDraftUid(null)
    setResults([])
    setOpen(false)
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setOpen(false)
  }

  const runSearch = (q) => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      searchUsers(q, 8)
        .then((d) => {
          setResults(d)
          setOpen(true)
        })
        .catch(() => setResults([]))
    }, 250)
  }

  const pickUser = (u) => {
    setDraft(u.name)
    setDraftUid(u.uid)
    setOpen(false)
  }

  const save = () => {
    const { formData: id } = resolve(props.idLocation ?? '~.id')
    const { formData: bundle } = resolve(props.bundleLocation ?? '~.type')
    if (id == null || !bundle) {
      console.error('InlineEditCell: missing entity id or bundle, cannot save.')
      return
    }

    let payload
    if (mode === 'user') {
      if (draftUid == null) {
        cancel()
        return
      }
      payload = { [uidField]: draftUid }
    } else {
      payload = { [field]: draft }
    }

    const url =
      `${saveUrl}?display=${encodeURIComponent(display)}` +
      `&entity_type=${encodeURIComponent(entityType)}` +
      `&bundle=${encodeURIComponent(bundle)}` +
      `&entity_id=${encodeURIComponent(id)}`

    setBusy(true)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      })
      .then(() => {
        // Commit the new display value into the row for instant feedback.
        globalDataContext.updateData(draft, displayPath)
        setEditing(false)
        setOpen(false)
      })
      .catch((err) => {
        console.error('InlineEditCell: save failed', err)
        // eslint-disable-next-line no-alert
        alert("L'enregistrement a échoué.")
      })
      .finally(() => setBusy(false))
  }

  if (!editing) {
    return (
      <span className="group inline-flex items-center gap-1.5 max-w-full">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener"
            className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline truncate"
          >
            {displayValue || empty}
          </a>
        ) : (
          <span className="truncate">{displayValue || empty}</span>
        )}
        <button
          type="button"
          onClick={startEdit}
          title={props.editTitle ?? 'Éditer'}
          className="shrink-0 text-gray-300 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition"
        >
          <Pencil size={14} />
        </button>
      </span>
    )
  }

  return (
    <span className="relative inline-flex items-center gap-1.5">
      <input
        type="text"
        value={draft}
        autoFocus
        placeholder={mode === 'user' ? 'Rechercher un utilisateur…' : undefined}
        onChange={(e) => {
          setDraft(e.target.value)
          if (mode === 'user') {
            setDraftUid(null)
            runSearch(e.target.value)
          }
        }}
        onFocus={() => {
          if (mode === 'user' && draft) runSearch(draft)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel()
          if (e.key === 'Enter' && mode !== 'user') save()
        }}
        className="w-44 px-2 py-1 border border-indigo-400 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      {mode === 'user' && open && results.length > 0 && (
        <ul className="absolute top-full left-0 z-50 mt-1 w-56 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-auto">
          {results.map((u) => (
            <li key={u.uid}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  pickUser(u)
                }}
                className="block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50"
              >
                {u.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={save}
        disabled={busy || (mode === 'user' && draftUid == null)}
        title="Enregistrer"
        className="shrink-0 text-green-600 hover:text-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Check size={16} />
      </button>
      <button
        type="button"
        onClick={cancel}
        title="Annuler"
        className="shrink-0 text-gray-400 hover:text-gray-600"
      >
        <X size={16} />
      </button>
    </span>
  )
}
